import { ApiResult, PaginatedResponse, request, safeRequest } from '@synergy/api';

import { CompleteInvoiceRequest, InvoiceRequest } from './invoice-detail/models/invoice-request';
import { ExportInvoice } from './invoice-overview/models/export-invoice';
import { ExportInvoicesRequest } from './invoice-overview/models/export-invoices-request';
import { Invoice } from './models/invoice';
import { InvoicePositionsRequest } from './invoice-detail/models/invoice-positions-request';
import { Prefix } from '../../common/models/prefix';

export interface CompleteInvoiceResponse {
    id: number;
    number: string;
    status: string;
}

/**
 * Why an invoice was left alone. Mirrors Invoice::BLOCKER_* in the backend,
 * plus 'notFound' for an id with nothing behind it.
 */
export type SkippedInvoiceReason =
    | 'status'
    | 'creditAmount'
    | 'taxRate'
    | 'taxMismatch'
    | 'orderNumber'
    | 'prefixYear'
    | 'dateOrder'
    | 'noPosition'
    | 'empty'
    | 'notFound';

export interface SkippedInvoiceResponse {
    id: number;
    number: string | null;
    reason: SkippedInvoiceReason;
}

/** A batch may do part of its job; both halves come back named (#2136). */
export interface CompleteInvoicesResponse {
    completed: CompleteInvoiceResponse[];
    skipped: SkippedInvoiceResponse[];
}

export class InvoiceApi {
    constructor(protected baseUrl: string) {}

    public get(id: number): Promise<ApiResult<Invoice>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/invoices/${id}`),
            response => Invoice.fromPlainObject(response.data)
        );
    }

    public post(invoice: InvoiceRequest): Promise<ApiResult<Invoice>> {
        return safeRequest(
            () =>
                request.post(`${this.baseUrl}/invoices`, invoice, {
                    params: { isSync: true },
                }),
            response => Invoice.fromPlainObject(response.data)
        );
    }

    public patch(id: number, invoice: Partial<InvoiceRequest>): Promise<ApiResult<Invoice>> {
        return safeRequest(
            () => request.patch(`${this.baseUrl}/invoices/${id}`, invoice),
            response => Invoice.fromPlainObject(response.data)
        );
    }

    public getPositions(id: number): Promise<ApiResult<Invoice>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/invoices/${id}`),
            response => Invoice.fromPlainObject(response.data)
        );
    }

    public updatePositions(invoice: InvoicePositionsRequest): Promise<ApiResult<Invoice>> {
        return safeRequest(
            () => request.patch(`${this.baseUrl}/invoices/${invoice.id}/positions`, invoice),
            response => Invoice.fromPlainObject(response.data)
        );
    }

    public complete(ids: number[], params?: CompleteInvoiceRequest): Promise<ApiResult<CompleteInvoicesResponse>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/invoices/complete`, { ...params, ids }),
            response => response.data
        );
    }

    public cancel(id: number): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/invoices/${id}/cancel`, {}, { params: { isSync: true } }),
            () => null
        );
    }

    public duplicate(id: number): Promise<ApiResult<Invoice>> {
        return safeRequest(
            () =>
                request.post(
                    `${this.baseUrl}/invoices/${id}/copy`,
                    {},
                    {
                        params: { isSync: true },
                    }
                ),
            response => response.data
        );
    }

    public cget(params?: URLSearchParams): Promise<ApiResult<PaginatedResponse<Invoice>>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/invoices`, { params }),
            response => {
                return response.data;
            }
        );
    }

    public delete(ids: number[]): Promise<ApiResult<null>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/invoices/delete`, { ids }),
            () => null
        );
    }

    public export(values: ExportInvoicesRequest): Promise<ApiResult<string>> {
        return safeRequest(
            () => request.post(`${this.baseUrl}/receipt-export/invoice`, values),
            response => response.data
        );
    }

    public getLatestExport(prefix: Prefix): Promise<ApiResult<ExportInvoice>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/receipt-export/invoice-number-sequence-history/${prefix.id}/latest`),
            response => (response.status === 200 ? response.data : null)
        );
    }

    public downloadLatestExport(path: string): Promise<ApiResult<string>> {
        return safeRequest(
            () => request.get(`${this.baseUrl}/receipt-export/invoice/download/${path}`),
            response => response.data
        );
    }
}
